import { timeFormat } from 'd3-time-format';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { colors } from '../../../../../../../colorVars';
import { useWebsocketStore } from '../../../../../../../stores/websocketStore/websocketProvider';
import ApplyRounding from '../../../../../../../utils/math/ApplyRounding';
import { DownloadCsvComponent } from '../../../../../../atomic/buttons/downloadCSV/DownloadCsvComponent';
import ExpandButton from '../../../../../../atomic/buttons/expandButton/ExpandButton';
import { Body3, Body4 } from '../../../../../../atomic/text/Text';
import { headerLists } from './tableLists';

export const ClosedPositionsTable = observer(({ sortingKeyword }) => {
  const { closedPositionsStore } = useWebsocketStore();
  const sim = closedPositionsStore.closedPositions.sim[sortingKeyword];
  const live = closedPositionsStore.closedPositions.live[sortingKeyword];
  const formatDate = timeFormat('%m-%d-%Y');
  const formattedToday = formatDate(new Date());
  const simData = Object.values(sim);
  const liveData = Object.values(live);
  const [liveExpanded, setLiveExpanded] = useState(true);
  const [simExpanded, setSimExpanded] = useState(true);
  const headerList = headerLists[sortingKeyword];
  const formatTime = timeFormat('%Y-%m-%d');
  const today = new Date();
  const day = formatTime(today);

  return (
    <>
      <thead></thead>

      <tbody>
        <tr>
          <td
            colSpan={13}
            style={{
              border: '1px solid rgb(88, 88, 88)',
              minWidth: '600px',
            }}
          >
            <div
              style={{ display: 'grid', gridTemplateColumns: '2fr 7fr 8fr' }}
            >
              <div>
                <ExpandButton
                  stroke={2}
                  size={10}
                  color={colors.lightGrey}
                  direction="bottom"
                  expanded={liveExpanded}
                  handleClick={() => setLiveExpanded((prev) => !prev)}
                  display="inline"
                  margin="0 5px 0 5px"
                />
                <Body3
                  margin="0 18px 0 0"
                  fontWeight={700}
                  color={colors.lightGrey}
                >
                  Live
                </Body3>
              </div>
              <div>
                <Body4
                  margin="0 10px 0 0"
                  fontWeight={700}
                  color={colors.lightGrey}
                >
                  Cost:{' '}
                  {ApplyRounding(
                    closedPositionsStore.closedPositions.live.byDay[day]
                      ?.cost_basis ?? 0,
                    2
                  ) ?? 0}
                </Body4>
                <Body4
                  margin="0 10px 0 0"
                  fontWeight={700}
                  color={
                    closedPositionsStore.closedPositions.live.byDay[day]
                      ?.pnl === 0 ||
                    closedPositionsStore.closedPositions.live.byDay[day]
                      ?.pnl === undefined
                      ? colors.lightGrey
                      : closedPositionsStore.closedPositions.live.byDay[day]
                        ?.pnl > 0
                        ? colors.green
                        : colors.red
                  }
                >
                  PnL:{' '}
                  {ApplyRounding(
                    closedPositionsStore.closedPositions.live.byDay[day]?.pnl ??
                      0,
                    2
                  )}
                </Body4>
                <Body4
                  margin="0 10px 0 0"
                  fontWeight={700}
                  color={
                    closedPositionsStore.closedPositions.live.byDay[day]
                      ?.pnl === 0 ||
                    closedPositionsStore.closedPositions.live.byDay[day]
                      ?.pnl === undefined
                      ? colors.lightGrey
                      : closedPositionsStore.closedPositions.live.byDay[day]
                        ?.pnl > 0
                        ? colors.green
                        : colors.red
                  }
                >
                  %PnL:{' '}
                  {closedPositionsStore.closedPositions.live.byDay[day]
                    ?.percent_pnl ?? 0}
                </Body4>
                <Body4
                  margin="0 10px 0 0"
                  fontWeight={700}
                  color={colors.lightGrey}
                >
                  Trades:{' '}
                  {closedPositionsStore.closedPositions.live.byDay[day]
                    ?.totalTrades ?? 0}
                </Body4>
              </div>
              <DownloadCsvComponent
                headers={headerList}
                data={liveData}
                filename={`closed_positions_live_${formattedToday}.csv`}
                size="15px"
                backgroundColor={colors.blue}
                margin="5px 0 0 10px"
              />
            </div>
          </td>
        </tr>
      </tbody>

      {liveExpanded ? (
        <>
          <thead>
            <tr>
              {headerList.map((header) => {
                return (
                  <th key={header.label}>
                    <Body3 fontWeight={700} color={colors.lightGrey}>
                      {header.label}
                    </Body3>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {Object.keys(live)
              .sort((a, b) => {
                return (
                  live[sortingKeyword === 'byPosition' ? b : a].sortingField -
                  live[sortingKeyword === 'byPosition' ? a : b].sortingField
                );
              })
              .map((index) => {
                return (
                  <tr key={index}>
                    {headerList.map(({ key, applyRounding }) => {
                      let color = colors.lightGrey;
                      if (key === 'pnl' || key === 'percent_pnl') {
                        if (live[index][key] > 0) color = colors.green;
                        if (live[index][key] < 0) color = colors.red;
                      }
                      return (
                        <td key={key}>
                          <Body4 fontWeight={600} color={color}>
                            {applyRounding
                              ? ApplyRounding(live[index][key], 2)
                              : live[index][key]}
                          </Body4>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </>
      ) : null}
      <tbody>
        <tr>
          <td
            colSpan={13}
            style={{
              border: '1px solid rgb(88, 88, 88)',
              minWidth: '600px',
            }}
          >
            <div
              style={{ display: 'grid', gridTemplateColumns: '2fr 7fr 8fr' }}
            >
              <div>
                <ExpandButton
                  stroke={2}
                  size={10}
                  color={colors.lightGrey}
                  direction="bottom"
                  expanded={simExpanded}
                  handleClick={() => setSimExpanded((prev) => !prev)}
                  display="inline"
                  margin="0 5px 0 5px"
                />
                <Body3
                  margin="0 20px 0 0"
                  fontWeight={700}
                  color={colors.lightGrey}
                >
                  Sim
                </Body3>
              </div>
              <div>
                <Body4
                  margin="0 10px 0 0"
                  fontWeight={700}
                  color={colors.lightGrey}
                >
                  Cost:{' '}
                  {ApplyRounding(
                    closedPositionsStore.closedPositions.sim.byDay[day]
                      ?.cost_basis ?? 0,
                    2
                  ) ?? 0}
                </Body4>
                <Body4
                  margin="0 10px 0 0"
                  fontWeight={700}
                  color={
                    closedPositionsStore.closedPositions.sim.byDay[day]?.pnl ===
                      0 ||
                    closedPositionsStore.closedPositions.sim.byDay[day]?.pnl ===
                      undefined
                      ? colors.lightGrey
                      : closedPositionsStore.closedPositions.sim.byDay[day]
                        ?.pnl > 0
                        ? colors.green
                        : colors.red
                  }
                >
                  PnL:{' '}
                  {ApplyRounding(
                    closedPositionsStore.closedPositions.sim.byDay[day]?.pnl ??
                      0,
                    2
                  )}
                </Body4>
                <Body4
                  margin="0 10px 0 0"
                  fontWeight={700}
                  color={
                    closedPositionsStore.closedPositions.sim.byDay[day]?.pnl ===
                      0 ||
                    closedPositionsStore.closedPositions.sim.byDay[day]?.pnl ===
                      undefined
                      ? colors.lightGrey
                      : closedPositionsStore.closedPositions.sim.byDay[day]
                        ?.pnl > 0
                        ? colors.green
                        : colors.red
                  }
                >
                  %PnL:{' '}
                  {closedPositionsStore.closedPositions.sim.byDay[day]
                    ?.percent_pnl ?? 0}
                </Body4>
                <Body4
                  margin="0 10px 0 0"
                  fontWeight={700}
                  color={colors.lightGrey}
                >
                  Trades:{' '}
                  {closedPositionsStore.closedPositions.sim.byDay[day]
                    ?.totalTrades ?? 0}
                </Body4>
              </div>
              <DownloadCsvComponent
                headers={headerList}
                data={simData}
                filename={`closed_positions_sim_${formattedToday}.csv`}
                size="15px"
                backgroundColor={colors.blue}
                margin="5px 0 0 10px"
              />
            </div>
          </td>
        </tr>
      </tbody>
      {simExpanded ? (
        <>
          <thead>
            <tr>
              {headerList.map((header) => (
                <th key={header.label}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    {header.label}
                  </Body3>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(sim)
              .sort((a, b) => {
                return (
                  sim[sortingKeyword === 'byPosition' ? b : a].sortingField -
                  sim[sortingKeyword === 'byPosition' ? a : b].sortingField
                );
              })
              .map((index) => {
                return (
                  <tr key={index}>
                    {headerList.map(({ key, applyRounding }) => {
                      let color = colors.lightGrey;
                      if (key === 'pnl' || key === 'percent_pnl') {
                        if (sim[index][key] > 0) color = colors.green;
                        if (sim[index][key] < 0) color = colors.red;
                      }
                      return (
                        <td key={key}>
                          <Body4 fontWeight={600} color={color}>
                            {applyRounding
                              ? ApplyRounding(sim[index][key], 2)
                              : sim[index][key]}
                          </Body4>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </>
      ) : null}
    </>
  );
});

/*
{
    "1": {
        "positionId": 1,
        "tradingSystemId": "BASIC_TRADING_SYSTEM1644867044009",
        "symbol": "AAPL",
        "pnl": "2.300000",
        "quantity": "46",
        "date_acquired": "11:30:52",
        "date_exited": "11:31:04",
        "side": "BTO",
        "strategy": "BREAK",
        "strategyDescription": "ENTER_NOW",
        "tradePlanTemplate": "basic",
        "cost_basis": "7693.96",
        "percent_pnl": 0.03,
        "day": "2022-02-14",
        "sortingField": 1644867052122
    },
    "2": {
        "positionId": 2,
        "tradingSystemId": "BASIC_TRADING_SYSTEM1644867044010",
        "symbol": "AMD",
        "pnl": "0.670000",
        "quantity": "67",
        "date_acquired": "11:30:52",
        "date_exited": "11:31:06",
        "side": "BTO",
        "strategy": "BREAK",
        "strategyDescription": "ENTER_NOW",
        "tradePlanTemplate": "basic",
        "cost_basis": "7703.66",
        "percent_pnl": 0.01,
        "day": "2022-02-14",
        "sortingField": 1644867052918
    },
    "3": {
        "positionId": 3,
        "tradingSystemId": "BASIC_TRADING_SYSTEM1644867044011",
        "symbol": "TSLA",
        "pnl": "-1.200000",
        "quantity": "8",
        "date_acquired": "11:30:54",
        "date_exited": "11:31:07",
        "side": "BTO",
        "strategy": "BREAK",
        "strategyDescription": "ENTER_NOW",
        "tradePlanTemplate": "basic",
        "cost_basis": "6957.92",
        "percent_pnl": -0.02,
        "day": "2022-02-14",
        "sortingField": 1644867054609
    }
}


*/
